import React, { useEffect, useImperativeHandle, useState } from 'react';
import {
  BaseTable,
  features,
  useTablePipeline,
} from 'ali-react-table';
import { useIntl } from 'umi';
import { ConfigProvider, Pagination } from 'tea-component';
import classnames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { useSetupFeatures } from './hooks';
import { UseMonitorVisible } from '@/pages/intelligence/pages/TopCharts/Companies/components/GamesDetails/Financials/components/UseMonitorVisible';
import { ItemOrder, combineArrays, getPagingInterception, serialSortHandler } from './utils';
import {
  PAGE_INDEX,
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  TABLE_DEFAULT_HORIZONTAL_ROWS,
  TABLE_DEFAULT_STYLE,
  TABLE_DEFAULT_VERTICAL_ROWS,
} from './constant';
import Loading from '@/pages/intelligence/components/Loading';
import { CheckboxExclusiveAli } from './components/CheckboxExclusiveAli';
import { RadioExclusiveAli } from './components/RadioExclusiveAli';
import './style/index.scss';
import { isEmpty } from 'lodash-es';
import { autoColumnWidth } from './utils/autoColumnWidth';
import { IAutoColumnWidthOptions, ICommonBaseTableProps, ICommonBaseTableRef } from './_type';

export type ParamType<T, U = unknown> = T extends (param: infer P) => U ? P : T;

// 管道集合。目前只支持这些,可以自定义use，需要符合TablePipeline这个类型
export const featDict = {
  sort: features.sort,
  singleSelect: features.singleSelect,
  multiSelect: features.multiSelect,
  treeMode: features.treeMode,
  treeSelect: features.treeSelect,
  rowGrouping: features.rowGrouping,
  columnRangeHover: features.columnRangeHover,
  columnHover: features.columnHover,
  rowDetail: features.rowDetail,
  tips: features.tips,
  // 自动列宽调整功能
  autoColumnWidth: (options: IAutoColumnWidthOptions = {}) => (pipeline: any) => autoColumnWidth(pipeline, options),
  columnResize: features.columnResize,
  autoRowSpan: features.autoRowSpan,

  // 需要返回一个高价函数
  customFeatKey: {},
};

//  1.推荐设置 primaryKey 作为每一行数据的唯一标识字段
//  2.开启拖拽时，需要明确表格是基于window还是固定容器，不然会有don卡顿严重情况
//  3.使用pipeline这个机制时，可以use各种方法，只要符合(pipeline: TablePipeline) => TablePipeline，即可快速接入本项目
//  4.官网文档:https://ali-react-table.js.org/docs/,本项目大部分需求都可以实现，具体文档体现，亮点在强大的pipeline以及虚拟处理
//  5.迁移时，请注意使用SortHeaderCell

export const CommonBaseTable = React.forwardRef<
ICommonBaseTableRef,
ICommonBaseTableProps
>((props, ref) => {
  // 表格基本 props
  const { className, style, tableConfig, featConfig, dataSource, columns } =
    props;
  // UI 拓展（表格工具栏 & 翻页器等）
  const {
    // 表格高度
    tableHeight = 550,
    // 表格容器类名
    wrapperClassName,
    // 表格容器style
    wrapperStyle,
    // 分页配置
    pagination,
    // 自定义头部组件
    customHeaderElement,
    // 行id
    primaryKey,
    // 是否前台分页，不走接口
    isOpenForegroundPage = false,
    // 是否开启分页
    isShowPagination = true,
    // 是否开启默认排序，如果不需要，需自定义开发
    isOpenInitialSort = false,
    // 是否开启默认排序的情况下，只保留排序交互，数据来源于后端处理排序
    isSortKeepDataSource = false,
    // 是否开启默认排序的情况下，开启单列还是多列
    isOpenSortMultiple = false,
    // 是否开启默认排序的情况下且是多列的情况，设置排序状态等，默认受控
    isSetDefaultSort=[],
    // 是否开启默认排序的情况下，获取排序props等
    handleOnChangeSorts,
    /**
     * 为 true 时整表不包 UseMonitorVisible（Tab/折叠内等避免视口懒挂载首帧高度为 0）。
     * 与仅传 isDestroyElement: false 不同：彻底绕过该层，不依赖其内部实现。
     */
    skipViewportVisibilityLazy,
    // 其他自定义
    ...restProps
  } = tableConfig || {};
  const {
    pageSize,
    pageIndex,
    total,
    onPageSizeChange,
    pageSizeOptions,
    ...pageProps
  } = pagination || {};
  // console.log(props);
  // 中英切换方法
  const { locale, formatMessage } = useIntl(); 
  const [sort,setSort]=useState<ItemOrder[]>([]);
  const teaLocale = locale === 'zh-CN' ? 'zh' : 'en';
  // 通过useTablePipeline导出pipeline实例,进行各种功能组合
  const pipeline = useTablePipeline({
    components: { Checkbox: CheckboxExclusiveAli, Radio: RadioExclusiveAli },
  })
    .input({ dataSource, columns })
    .primaryKey(primaryKey);
  // 传入的featConfig,循环调用，省却链式调用use
  useSetupFeatures(pipeline, featConfig   as { [key: string]: any; });
        
  useEffect(() => {
    if(isEmpty(isSetDefaultSort)) return;
    setSort(isSetDefaultSort);
  }, [isSetDefaultSort]);
        
  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    pipeline,
  }) as any);
  // 主要针对多列排序时,重新调整排序状态UI
  const handleActionsSortChange = useMemoizedFn(
    (value: ItemOrder[], task: ItemOrder,) => {
      handleOnChangeSorts?.(combineArrays(sort || [], value || [], task));
      if (isOpenSortMultiple) {
        setSort(combineArrays(sort || [], value || [], task));
      }
    }
  );
  if (isOpenInitialSort) {
    pipeline.use(serialSortHandler(isSortKeepDataSource, handleActionsSortChange, sort, isOpenSortMultiple ));
  } else if (restProps.isLoading) {
    pipeline.dataSource([]);
  } 
  const tabularRawData = pipeline.getProps();
  if (isOpenForegroundPage) {
    pipeline.dataSource(
      getPagingInterception(tabularRawData?.dataSource, pageSize)[
        pageIndex - 1
      ],
    );
  }
  // 这里重新计算一下开启虚拟滚动的条件
  const isOpenVertical =
    tabularRawData.dataSource?.length >= TABLE_DEFAULT_VERTICAL_ROWS;
  const isOpenHorizontal =
    tabularRawData.columns?.length >= TABLE_DEFAULT_HORIZONTAL_ROWS;
  // 需要注意，这个组件库不支持透传props,请遵循ts规则进行传递
  const defaultConfig = {
    components: {
      EmptyContent: () => (
        <>
          {formatMessage({
            id: 'dbi_store_rank_no_data_under_current_selections',
          })}
        </>
      ),
      LoadingIcon: () => (
        <div className="loadingContainer">
          <Loading />
        </div>
      ),
    },
    defaultColumnWidth: 150,
    useVirtual: {
      horizontal: isOpenHorizontal,
      vertical: isOpenVertical,
      header: isOpenVertical && isOpenHorizontal,
    },
    // 进行覆盖
    ...restProps,
  };
  const tableShell = (
    <div
      className={classnames('db-alitable-container', wrapperClassName)}
      style={wrapperStyle}
    >
      <BaseTable
        ref={ref}
        className={classnames('db-alitable-wrapper', className)}
        style={{
          maxHeight: tableHeight,
          width: '100%',
          overflow: 'auto',
          ...TABLE_DEFAULT_STYLE,
          ...style,
        }}
        // 表格配置
        {...defaultConfig}
        // 表格数据源
        {...pipeline.getProps()}
      />
      {/* 分页配置等 */}
      {isShowPagination && (
        <ConfigProvider locale={teaLocale}>
          <Pagination
            pageSizeOptions={pageSizeOptions || PAGE_SIZE_OPTIONS}
            onPagingChange={(e) => {
              onPageSizeChange?.(e);
            }}
            pageSize={pageSize || PAGE_SIZE}
            pageIndex={pageIndex || PAGE_INDEX}
            recordCount={total}
            stateTextVisible={true}
            stateText={formatMessage(
              { id: 'dbi_pagination_total' },
              { total },
            )}
            {...pageProps}
          />
        </ConfigProvider>
      )}
    </div>
  );
  if (skipViewportVisibilityLazy) {
    return tableShell;
  }
  return <UseMonitorVisible>{tableShell}</UseMonitorVisible>;
});
